/**
 * Localized date and time formatters.
 *
 * Numbers and date layouts (order, separators) match US conventions across
 * languages — only the words (month names, day names, "TODAY"/"YESTERDAY",
 * "ago" labels) are translated.
 */
import i18n from '../i18n';

const getArray = (key) => {
  const value = i18n.t(key, { returnObjects: true, ns: 'dates' });
  return Array.isArray(value) ? value : [];
};

export const monthLong = (monthIdx) => getArray('months.long')[monthIdx] || '';
export const monthLongUpper = (monthIdx) => getArray('months.longUpper')[monthIdx] || '';
export const monthShort = (monthIdx) => getArray('months.short')[monthIdx] || '';
export const monthShortUpper = (monthIdx) => getArray('months.shortUpper')[monthIdx] || '';

export const dayLong = (dayIdx) => getArray('days.long')[dayIdx] || '';
export const dayShort = (dayIdx) => getArray('days.short')[dayIdx] || '';
export const dayShortUpper = (dayIdx) => getArray('days.shortUpper')[dayIdx] || '';

export const labelToday = () => i18n.t('today', { ns: 'dates' });
export const labelYesterday = () => i18n.t('yesterday', { ns: 'dates' });

/**
 * "Just now" / "X minutes ago" / etc. — translated but order preserved.
 */
// Parse a date-only string ("2023-09-17") as LOCAL Y/M/D — avoids the UTC
// off-by-one that `new Date("2023-09-17")` causes in negative timezones.
const parseYMD = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return { y, m: m - 1, d };
};

// "Sep 17, 2023" — US layout, localized month word.
export const formatDate = (s) => {
  const p = parseYMD(s);
  return p ? `${monthShort(p.m)} ${p.d}, ${p.y}` : '';
};

/**
 * The compact form of a plain calendar date — "Sep 17", or "Sep 17, 2023"
 * outside the current year (MI-336).
 *
 * Deliberately separate from `formatDateShort`, which parses through `Date`
 * because it is given a timestamp. A movement's date is date-only, and read
 * that way it lands on the previous evening west of Greenwich — which is how
 * a row under "AGOSTO 18" came to print "Ago 17".
 */
export const formatDayShort = (s) => {
  const p = parseYMD(s);
  if (!p) return '';
  return p.y === new Date().getFullYear()
    ? `${monthShort(p.m)} ${p.d}`
    : `${monthShort(p.m)} ${p.d}, ${p.y}`;
};

/**
 * "Sep 17, 2023, 2:30:09 p. m." — the full stamp behind a compact date.
 *
 * Takes a timestamp rather than a plain date, so it parses through `Date`
 * where `formatDate` deliberately does not: a `YYYY-MM-DD` read as UTC lands
 * on the previous evening west of Greenwich, but a stamp carries its own zone
 * and is meant to be shown in the reader's.
 */
export const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString(i18n.language || 'en-US', {
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
  });
  return `${monthShort(d.getMonth())} ${d.getDate()}, ${d.getFullYear()}, ${time}`;
};

/**
 * The compact half of the same stamp — "Sep 17", or "Sep 17, 2023" when the
 * year is not this one. A bare "Sep 17" beside a movement dated "Dic 30, 2025"
 * would look like the same year, and for an imported statement it rarely is.
 */
export const formatDateShort = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return sameYear
    ? `${monthShort(d.getMonth())} ${d.getDate()}`
    : `${monthShort(d.getMonth())} ${d.getDate()}, ${d.getFullYear()}`;
};

// Statement coverage period → "Sep 17 – Oct 16, 2023" (shared year shown once),
// or the full range across years. Falls back to whichever endpoint exists.
export const formatPeriodRange = (start, end) => {
  const a = parseYMD(start);
  const b = parseYMD(end);
  if (a && b) {
    return a.y === b.y
      ? `${monthShort(a.m)} ${a.d} – ${monthShort(b.m)} ${b.d}, ${b.y}`
      : `${monthShort(a.m)} ${a.d}, ${a.y} – ${monthShort(b.m)} ${b.d}, ${b.y}`;
  }
  return formatDate(end || start);
};

// Chart bucket label, by the grain actually drawn (MI-307):
//   day   → "AUG 5, 2026"
//   week  → "AUG 3 – AUG 9"   (the ISO week starting on that Monday)
//   month → "AUG 2026"
export const formatPeriodLabel = (period, granularity = 'month') => {
  const p = parseYMD(period);
  if (!p) return '';
  if (granularity === 'day') {
    return `${monthShortUpper(p.m)} ${p.d}, ${p.y}`;
  }
  if (granularity === 'week') {
    const end = new Date(p.y, p.m, p.d + 6);
    return `${monthShortUpper(p.m)} ${p.d} – ${monthShortUpper(end.getMonth())} ${end.getDate()}`;
  }
  return `${monthShortUpper(p.m)} ${p.y}`;
};

// The backend sends naive UTC timestamps; mark them as such before parsing.
const parseTimestamp = (dateString) => {
  if (!dateString) return null;
  let dateStr = dateString;
  if (!dateStr.includes('Z') && !dateStr.includes('+') && !dateStr.includes('-00:')) {
    dateStr = dateStr + 'Z';
  }
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
};

// "October 7, 2026" — a whole date in words, for a sentence ("expires on …").
// Takes a timestamp, shown on the reader's own calendar day.
export const formatDateLong = (dateString) => {
  const d = parseTimestamp(dateString);
  return d ? `${monthLong(d.getMonth())} ${d.getDate()}, ${d.getFullYear()}` : '';
};

// Compact age ("10min", "5h", "27d", "1mes") for dense lists like the chat
// history, where "hace 27 días" costs three times the width for no more
// meaning. Use formatRelativeAgo where there's room for the full phrase.
export const formatRelativeShort = (dateString) => {
  const date = parseTimestamp(dateString);
  if (!date) return '';

  const diffMinutes = Math.floor((new Date() - date) / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const short = (unit, count) => i18n.t(`short.${unit}`, { ns: 'dates', count });

  if (diffMinutes < 1) return short('now');
  if (diffMinutes < 60) return short('minutes', diffMinutes);
  if (diffHours < 24) return short('hours', diffHours);
  if (diffDays < 30) return short('days', diffDays);

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return short('months', diffMonths);
  return short('years', Math.floor(diffDays / 365));
};

export const formatRelativeAgo = (dateString) => {
  if (!dateString) return '';

  let dateStr = dateString;
  if (dateStr && !dateStr.includes('Z') && !dateStr.includes('+') && !dateStr.includes('-00:')) {
    dateStr = dateStr + 'Z';
  }
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return i18n.t('relative.justNow', { ns: 'dates' });
  if (diffMinutes < 60) return i18n.t(diffMinutes === 1 ? 'relative.minutesOne' : 'relative.minutesOther', { ns: 'dates', count: diffMinutes });
  if (diffHours < 24) return i18n.t(diffHours === 1 ? 'relative.hoursOne' : 'relative.hoursOther', { ns: 'dates', count: diffHours });
  if (diffDays < 30) return i18n.t(diffDays === 1 ? 'relative.daysOne' : 'relative.daysOther', { ns: 'dates', count: diffDays });

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return i18n.t(diffMonths === 1 ? 'relative.monthsOne' : 'relative.monthsOther', { ns: 'dates', count: diffMonths });

  const diffYears = Math.floor(diffDays / 365);
  return i18n.t(diffYears === 1 ? 'relative.yearsOne' : 'relative.yearsOther', { ns: 'dates', count: diffYears });
};

/**
 * A day header, Copilot-style: "MON, MARCH 30", or "today"/"yesterday".
 *
 * Shared so the grouped view's day headers read identically to the ungrouped
 * list's — the two show the same thing and should not look like two features.
 */
export function dateToWords(
  dateString,
  { alwaysShowYear = false, relative = true } = {},
) {
  const [year, month, day] = dateString.split('-').map(Number);
  const inputDate = new Date(year, month - 1, day);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today - inputDate) / (1000 * 60 * 60 * 24));

  // MI-336: the two used to be one flag, which is why the day header could
  // not have both the year and "today"/"yesterday". They are separate
  // questions — whether a standalone header needs its year, and whether the
  // two days that have names get to use them — so they are separate flags.
  if (relative) {
    if (diffDays === 0) return i18n.t('today', { ns: 'dates' });
    if (diffDays === 1) return i18n.t('yesterday', { ns: 'dates' });
  }

  const shortDays = i18n.t('days.shortUpper', { ns: 'dates', returnObjects: true });
  const months = i18n.t('months.longUpper', { ns: 'dates', returnObjects: true });

  const dayOfWeek = shortDays[inputDate.getDay()];
  const currentYear = new Date().getFullYear();
  // Day headers are standalone rather than a continuous run of dates, so the
  // year has to be on each one — otherwise a collapsed "AGOSTO 19" could be
  // any August.
  const showYear = alwaysShowYear || year !== currentYear;

  return showYear
    ? `${dayOfWeek}, ${months[month - 1]} ${day}, ${year}`
    : `${dayOfWeek}, ${months[month - 1]} ${day}`;
}

/**
 * "7:02 AM" — a time of day on the 12h clock, in both languages: only the
 * words change between languages, and a clock has none.
 */
export const formatTime12h = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h % 12 || 12}:${m} ${h < 12 ? 'AM' : 'PM'}`;
};
